import type { TCardFacet, TCollectionData, TFacet } from '@/types/collection'
import { useEffect, useRef } from 'react'
import { ConcatSlugHelper } from './concat-slug.helper'
import { findMatchingFacetValueWithPriority } from './facet-matching'

export type Filters = Record<string, string | string[] | number | undefined>

export const deriveEffectiveInputs = (
  serverData: TCollectionData,
  storeOriginalSlug?: string,
  storeFilters?: Filters,
  storeConcat?: string | null,
  initialFilters: Filters = {}
) => {
  const fallbackOriginalSlug = serverData?.slug_analysis?.seo_alias || ''
  const facets = (serverData?.collection_detail?.facets || []) as TFacet[]
  const cardFacets = (serverData?.collection_detail?.card_facets ||
    []) as TCardFacet[]

  let fallbackConcat: string | null = null
  if (typeof window !== 'undefined' && fallbackOriginalSlug) {
    const { normalized } = ConcatSlugHelper.extractAndNormalize(
      window.location.pathname,
      fallbackOriginalSlug,
      facets,
      cardFacets
    )
    fallbackConcat = normalized
  }

  const effectiveOriginalSlug = storeOriginalSlug || fallbackOriginalSlug

  // Check if we're navigating to a DIFFERENT collection
  // If so, we should use initialFilters (from URL) instead of old storeFilters
  const isCollectionChanged =
    storeOriginalSlug && storeOriginalSlug !== fallbackOriginalSlug

  const hasStoreFilters = Object.keys(storeFilters || {}).length > 0
  const mappedInitialFilters: Filters = { ...initialFilters }

  // Map concat slug to filter if present (only when no store filters or collection changed)
  // Use priority helper to ensure color is prioritized
  if (fallbackConcat && (!hasStoreFilters || isCollectionChanged)) {
    const match = findMatchingFacetValueWithPriority(
      fallbackConcat,
      facets,
      cardFacets
    )
    if (match) {
      mappedInitialFilters[match.facet.seo_alias] = match.value.seo_alias
    }
  }

  // If collection changed, always use initialFilters (from URL)
  // Otherwise use existing logic: prefer store filters if available
  const effectiveFilters = isCollectionChanged
    ? mappedInitialFilters
    : hasStoreFilters
      ? (storeFilters as Filters)
      : mappedInitialFilters

  // If collection changed, reset concat value to use the new one from URL
  const effectiveConcat = isCollectionChanged
    ? fallbackConcat
    : (storeConcat ?? fallbackConcat)

  // Derive concatValue from effectiveFilters
  // Logic: Lock first value - only allow changing concatValue if clicking the same filter (same facet)
  const CONCAT_FACET_PRIORITY = ['color', 'mau-sac']
  const allFacets = [...facets, ...cardFacets]
  const concatFacets = allFacets.filter((f) => f.is_concat_slug)

  // Deduplicate by seo_alias
  const seenAliases = new Set<string>()
  const uniqueFacets = concatFacets.filter((f) => {
    if (seenAliases.has(f.seo_alias)) return false
    seenAliases.add(f.seo_alias)
    return true
  })

  // Sort by priority: color first
  const sortedFacets = uniqueFacets.sort((a, b) => {
    const priorityA = CONCAT_FACET_PRIORITY.indexOf(a.seo_alias)
    const priorityB = CONCAT_FACET_PRIORITY.indexOf(b.seo_alias)
    if (priorityA >= 0 && priorityB >= 0) return priorityA - priorityB
    if (priorityA >= 0) return -1
    if (priorityB >= 0) return 1
    return 0
  })

  let finalConcatValue = effectiveConcat

  // If we have an existing concatValue, check if user clicked the same filter (same facet)
  // Only allow changing concatValue if clicking the same concat filter
  if (effectiveConcat) {
    // Find which facet the current concatValue belongs to
    let currentConcatFacetKey: string | null = null
    for (const facet of sortedFacets) {
      const matchingValue = facet.facet_values?.find(
        (v) => v.seo_alias === effectiveConcat
      )
      if (matchingValue) {
        currentConcatFacetKey = facet.seo_alias
        break
      }
    }

    // If we found the facet, check if user clicked the same filter
    if (currentConcatFacetKey) {
      const filterValue = effectiveFilters[currentConcatFacetKey]
      if (filterValue) {
        // User clicked the same filter → check if should update concatValue
        const values = Array.isArray(filterValue)
          ? filterValue
          : [String(filterValue)]
        const currentFacet = sortedFacets.find(
          (f) => f.seo_alias === currentConcatFacetKey
        )
        const isAllowMultiple = currentFacet?.is_allow_multiple || false

        // If multiple values AND facet allows multiple: NO concat
        if (values.length > 1 && isAllowMultiple) {
          finalConcatValue = null
        } else {
          // Single value OR facet doesn't allow multiple: Set first value as concat
          finalConcatValue = values[0]
        }
        // User clicked the same filter → allow changing concatValue to first value
        // finalConcatValue = Array.isArray(filterValue)
        //   ? filterValue[0]
        //   : String(filterValue)
      } else {
        // User clicked different filter or cleared this filter → keep current concatValue
      }
    }
  } else {
    // No existing concatValue → derive from filters (first value of first concat facet)
    for (const facet of sortedFacets) {
      const filterValue = effectiveFilters[facet.seo_alias]
      if (filterValue) {
        const values = Array.isArray(filterValue)
          ? filterValue
          : [String(filterValue)]
        const isAllowMultiple = facet.is_allow_multiple || false

        // If multiple values AND facet allows multiple: NO concat
        if (values.length > 1 && isAllowMultiple) {
          finalConcatValue = null
        } else {
          // Single value OR facet doesn't allow multiple: Use first value as concat
          finalConcatValue = values[0]
        }
        // finalConcatValue = Array.isArray(filterValue)
        //   ? filterValue[0]
        //   : String(filterValue)
        break
      }
    }
  }

  return {
    effectiveOriginalSlug,
    effectiveFilters,
    effectiveConcat: finalConcatValue,
    facets,
    cardFacets,
  }
}

export const useInitialDataOnce = (shouldUse: boolean) => {
  const usedRef = useRef(false)
  useEffect(() => {
    if (shouldUse) {
      usedRef.current = true
    }
  }, [shouldUse])
  return usedRef
}

export const hasActiveFilter = (
  filters: Filters | undefined,
  facetKey: string
): boolean => {
  if (!filters?.[facetKey]) return false

  const filterValue = filters[facetKey]
  return Array.isArray(filterValue)
    ? filterValue.length > 0
    : filterValue !== '' && filterValue !== null && filterValue !== undefined
}

export const getActiveFilterSections = (
  facets: TFacet[],
  filters: Filters | undefined
): string[] => {
  if (!facets || facets.length === 0) return []

  const openSections: string[] = []

  // Luôn mở filter đầu tiên
  if (facets.length > 0) {
    const firstFacet = facets[0].seo_alias
    openSections.push(firstFacet)
  }

  // Kiểm tra từng facet xem có filter active không (ngoại trừ filter đầu tiên)
  facets.forEach((facet) => {
    const isActive = hasActiveFilter(filters, facet.seo_alias)
    if (isActive && !openSections.includes(facet.seo_alias)) {
      openSections.push(facet.seo_alias)
    }
  })

  return openSections
}

/**
 * Compare two filter objects to check if they are equal
 * Ignores 'page' and 'sort' keys
 */
export const areFiltersEqual = (
  filters1: Filters,
  filters2: Filters
): boolean => {
  const keys1 = Object.keys(filters1).filter(
    (k) => k !== 'page' && k !== 'sort'
  )
  const keys2 = Object.keys(filters2).filter(
    (k) => k !== 'page' && k !== 'sort'
  )

  if (keys1.length !== keys2.length) return false

  return keys1.every((key) => {
    const val1 = filters1[key]
    const val2 = filters2[key]

    // Compare arrays
    if (Array.isArray(val1) && Array.isArray(val2)) {
      return val1.length === val2.length && val1.every((v, i) => v === val2[i])
    }

    return val1 === val2
  })
}
